import { Button } from '@/design-system/button';
import { FileUpload } from '@/design-system/file-upload';
import { FileWithPreview, formatBytes } from '@/hooks/use-file-upload';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import CameraIcon from '@/icons/camera-icon';
import { cn } from '@/lib/utils';
import React from 'react';

type Upload = {
  label?: string;
  variant?: 'circle' | 'rect' | 'circle-button' | 'document' | 'imageWithDescription';
  className?: string;
  dropZoneClassName?: string;
  previewClassName?: string;
  showPlaceholderText?: boolean;
  isRequired?: boolean;
  buttonText?: string;
  children?: React.ReactNode;
  alt?: string;
  name?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  required?: boolean;
  maxSizeMB?: number;
  error?: string;
  defaultPreviewUrl?: string;
  defaultPreviewName?: string;
  defaultPreviewSize?: number;
  onDefaultPreviewRemove?: () => void;
  onFilesChange: (files: FileWithPreview[]) => void;
} & Record<string, unknown>;

const getFileNameFromUrl = (url: string) => {
  try {
    return decodeURIComponent(url.split('/').pop() ?? '');
  } catch {
    return '';
  }
};

export const Upload: React.FC<Upload> = ({
  label,
  variant = 'rect',
  className = '',
  dropZoneClassName = 'bg-surface-subtle',
  previewClassName = 'object-cover border rounded-sm h-[88px] w-[88px]',
  showPlaceholderText = true,
  buttonText,
  children,
  isRequired = false,
  accept,
  multiple,
  maxSizeMB = 25,
  alt = '',
  error,
  onFilesChange,
  defaultPreviewUrl = '',
  defaultPreviewName = '',
  defaultPreviewSize,
  onDefaultPreviewRemove,
  ...rest
}) => {
  const isCircle = variant === 'circle';
  const isCircleButton = variant === 'circle-button';
  const { t } = useDynamicLocale();

  if (isCircleButton) {
    return (
      <FileUpload
        className={`flex flex-row justify-between items-center ${className}`}
        variant={variant}
        maxSizeMB={maxSizeMB}
        accept={accept}
        onFilesChange={onFilesChange}
        multiple={multiple}
        {...rest}
      >
        <FileUpload.DropZone className={dropZoneClassName}>
          <FileUpload.Input />
          <FileUpload.Preview fallback={<FileUpload.Placeholder />} />
        </FileUpload.DropZone>

        <div>
          {label && (
            <div className="flex">
              <FileUpload.ProfileLabel>
                <Translation id={label} />
              </FileUpload.ProfileLabel>
              {isRequired && <span className="text-destructive-text leading-[100%]">*</span>}
            </div>
          )}
          <FileUpload.FileRequirement />
        </div>

        <FileUpload.CircleButtonActions />
        <FileUpload.Error />
        {children}
      </FileUpload>
    );
  }
  const hasDefaultPreview = !!defaultPreviewUrl;
  const fallbackName = defaultPreviewName || getFileNameFromUrl(defaultPreviewUrl) || 'Uploaded file';
  const fallbackSize = defaultPreviewSize ? formatBytes(defaultPreviewSize) : '0 Bytes';

  return (
    <FileUpload
      className={className}
      variant={variant}
      accept={accept}
      multiple={multiple}
      maxSizeMB={maxSizeMB}
      onFilesChange={onFilesChange}
      {...rest}
    >
      {label && (
        <div className="flex">
          <FileUpload.ProfileLabel className="text-left">
            <Translation id={label} />
          </FileUpload.ProfileLabel>
          {isRequired && <span className="text-destructive-text leading-[100%]">*</span>}
        </div>
      )}
      <FileUpload.DropZone
        className={cn(
          dropZoneClassName,
          isCircle && 'border !border-surface-subtle relative',
          defaultPreviewUrl && '!p-0',
          error && 'border-error-emphasis',
        )}
      >
        <FileUpload.Input />

        {hasDefaultPreview ? (
          <span className="">
            <div className={`${variant === 'circle' ? 'rounded-full w-[164px]' : ''}`}>
              {variant === 'document' ? (
                <div
                  className={`relative flex items-center gap-4 border border-dashed overflow-hidden border-border-strong rounded-md p-4 w-full`}
                >
                  <div className="relative w-20 flex-shrink-0">
                    <img
                      src={defaultPreviewUrl}
                      alt={alt ?? 'uploaded'}
                      className="w-[88px] h-[88px] object-cover rounded-sm border"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDefaultPreviewRemove?.();
                      }}
                      className="absolute -top-2 cursor-pointer -right-2 w-5 h-5 rounded-full bg-error-emphasis text-on-color text-xs flex items-center justify-center"
                    >
                      ✕
                    </button>
                  </div>
                  <span className="text-sm font-medium text-foreground-heading">
                    {getFileNameFromUrl(defaultPreviewUrl)}
                  </span>
                </div>
              ) : variant === 'imageWithDescription' ? (
                <div className="w-full rounded-md bg-surface-subtle px-4 py-4.5">
                  <div className="flex items-center gap-3.5">
                    <div className="relative">
                      <img
                        src={defaultPreviewUrl}
                        alt={alt ?? 'uploaded'}
                        className="h-22 w-22 shrink-0 rounded-sm border border-border-subtle object-cover"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDefaultPreviewRemove?.();
                        }}
                        className="absolute -top-2 -right-2 cursor-pointer w-5 h-5 rounded-full bg-error-emphasis text-on-color text-xs flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate max-w-[372px] text-xs font-medium text-primary">{fallbackName}</p>
                      <p className="mt-1 text-micro text-muted-foreground">{fallbackSize}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={defaultPreviewUrl}
                    alt={alt ?? 'uploaded'}
                    className={cn('object-cover', previewClassName, variant === 'circle' && 'rounded-full')}
                  />

                  {variant === 'circle' ? (
                    <CameraIcon
                      onClick={(e) => {
                        if (e?.preventDefault) e.preventDefault();
                        if (e?.stopPropagation) e.stopPropagation();
                        onDefaultPreviewRemove?.();
                      }}
                      className="rounded-full cursor-pointer text-on-color absolute right-2 top-30"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDefaultPreviewRemove?.();
                      }}
                      className="absolute -top-2 -right-2 cursor-pointer w-5 h-5 rounded-full bg-error-emphasis text-on-color text-xs flex items-center justify-center"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
            </div>
          </span>
        ) : (
          // ✅ normal FileUpload preview / placeholder flow
          <FileUpload.Preview
            className={isCircle ? '' : previewClassName}
            fallback={
              isCircle ? (
                <FileUpload.Placeholder />
              ) : (
                <FileUpload.Placeholder>
                  {showPlaceholderText && (
                    <div className="font-normal text-xs">
                      <p>{t('configurationManagement.location.page.editModal.dragAndDrop')}</p>
                      <div className="mt-2 font-medium">
                        <p>{t('configurationManagement.location.page.editModal.or')}</p>
                      </div>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    className="mt-2 text-xs p-2 font-medium bg-primary text-primary-foreground hover:text-primary-foreground"
                    type="button"
                  >
                    {buttonText ? buttonText : t('common.page.button.browseFiles')}
                  </Button>
                </FileUpload.Placeholder>
              )
            }
          />
        )}

        {!hasDefaultPreview && <FileUpload.RemoveButton />}
      </FileUpload.DropZone>
      {error && (
        <div className="text-destructive-text flex justify-start font-medium w-full text-sm">
          <Translation id={error} />
        </div>
      )}
      {children}
    </FileUpload>
  );
};
